/**
 * Skeleton — shimmer placeholder primitive shared by every loading state.
 * The shape modifier owns the radius and the sweep; feature CSS sizes each
 * block through `className` so placeholders line up with the real layout.
 */
import type { PropsWithChildren } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import './skeleton.css';

export type SkeletonShape = 'line' | 'circle' | 'rect';

interface SkeletonProps {
  shape?: SkeletonShape;
  className?: string;
}

export function Skeleton({ shape = 'line', className = '' }: SkeletonProps) {
  return <span className={`skeleton skeleton--${shape}${className ? ` ${className}` : ''}`} aria-hidden />;
}

interface SkeletonGroupProps {
  className?: string;
  /** Overrides the announced wait, e.g. "Loading your feed...". */
  label?: string;
}

/** Wraps placeholders and announces the wait once, instead of per block. */
export function SkeletonGroup({ className = '', label, children }: PropsWithChildren<SkeletonGroupProps>) {
  const { t } = useTranslation();

  return (
    <div className={`skeleton-group${className ? ` ${className}` : ''}`} role="status" aria-busy="true">
      <span className="sr-only">{label ?? t('common.loading')}</span>
      {children}
    </div>
  );
}

/** Stable keys for placeholder rows, which have no data to key on. */
export const skeletonKeys = (count: number): string[] =>
  Array.from({ length: count }, (_, index) => `skeleton-${index}`);
